/**
 * Копирайт и витринные данные публичного лендинга (`/`).
 *
 * Единственный источник формулировок ценностного предложения — сюда же
 * сверяется онбординг iOS (см. requirements.md, раздел 7). Все числа здесь —
 * иллюстративные константы: лендинг не ходит в API и не показывает реальные
 * данные пользователя (SEC-1/SEC-2).
 */

import {getConfig} from '../../support/Config';

/**
 * Куда ведёт основной CTA: на регистрацию (auth-registration-recovery, FR-25).
 * Вторичные ссылки «Войти» ведут на {@link LOGIN_HREF}.
 */
export const CTA_HREF = '/register';

/** Форма входа — для тех, у кого аккаунт уже есть. */
export const LOGIN_HREF = '/login';

/** Главная приложения: сюда лендинг ведёт того, кто уже вошёл. */
export const APP_HREF = '/home';

/** Формулировки CTA для авторизованного посетителя: регистрация ему не нужна. */
export const AUTHED_CTA = {
    primary: 'Перейти в гардероб',
    secondary: 'Мой гардероб',
} as const;

/**
 * Имя продукта из runtime-конфига (`/config.json`). В деве и тестах файла нет,
 * поэтому на публичной странице подставляем бренд, а не пустую строку.
 */
export const appName = (): string => getConfig('appName') || 'Clothes';

/** Версия сборки; пустая, если конфиг ещё не загружен — тогда её просто не показываем. */
export const appVersion = (): string => getConfig('appVersion') || '';

export const HERO = {
    titleLead: 'Ваш гардероб.',
    titleSecond: 'Теперь он умеет',
    titleAccent: 'думать.',
    description:
        'Clothes помогает вести учёт одежды, собирает образы под погоду и повод ' +
        'и подсказывает, что надеть, — опираясь на вещи, которые у вас уже есть.',
    primaryCta: 'Создать мой шкаф',
    secondaryCta: 'Посмотреть, как работает',
} as const;

export const HERO_BENEFITS = [
    'Добавление за минуту',
    'AI-рекомендации',
    'Экономия времени',
    'Забыть про «нечего надеть»',
] as const;

export const PROBLEM = {
    titleLead: 'Полный шкаф.',
    titleSecond: 'А надеть всё равно',
    titleAccent: 'нечего.',
    lead:
        'Вещи копятся, а носится одно и то же. Clothes показывает, что у вас есть, ' +
        'и превращает это в готовые сочетания.',
} as const;

export interface Pillar {
    id: string;
    title: string;
    text: string;
}

export const PILLARS: Pillar[] = [
    {
        id: 'inventory',
        title: 'Учёт вещей',
        text: 'Фото, ссылка или пара полей — вещь в гардеробе. AI сам заполнит остальное.',
    },
    {
        id: 'outfits',
        title: 'Умные подборки',
        text: 'Образы под погоду, повод и настроение — только из ваших вещей.',
    },
    {
        id: 'access',
        title: 'Порядок и доступ',
        text: 'Гардероб с вами на всех устройствах и под контролем прав доступа.',
    },
    {
        id: 'weather',
        title: 'Погода и события',
        text: 'Подскажет, что надеть на встречу, прогулку или поездку.',
    },
];

export const CROSS_DEVICE = {
    titleLead: 'Весь шкаф —',
    titleSecond: 'в телефоне и браузере',
    text:
        'Фото, ссылки, заметки. Сортируйте, отмечайте носку, находите нужную вещь ' +
        'за секунды. Всё синхронизируется, где бы вы ни открыли приложение.',
    points: [
        {id: 'fast', title: 'Быстро и удобно'},
        {id: 'sync', title: 'Синхронизация'},
        {id: 'devices', title: 'Для всех устройств'},
        {id: 'personal', title: 'Персонализация'},
    ],
} as const;

export const AI_BLOCK = {
    titleLead: 'Не советы из интернета.',
    titleAccent: 'Советы по вашим вещам.',
    text:
        'AI анализирует ваш гардероб и предлагает образы на каждый день и на особые ' +
        'случаи: с учётом погоды, сезона и вашего стиля.',
    occasions: ['На каждый день', 'Свидание', 'Встреча', 'Офис', 'Путешествие', 'Активный отдых'],
} as const;

export const WEATHER_BLOCK = {
    title: 'Что надеть: погода и события',
    days: [
        {id: 'd1', label: 'Сегодня', temp: '+18°', kind: 'rain' as const},
        {id: 'd2', label: 'Завтра', temp: '+14°', kind: 'rain' as const},
        {id: 'd3', label: 'Пт', temp: '+16°', kind: 'cloud' as const},
        {id: 'd4', label: 'Сб', temp: '+20°', kind: 'sun' as const},
        {id: 'd5', label: 'Вс', temp: '+19°', kind: 'cloud' as const},
    ],
    outfitTitle: 'Рекомендуемый образ',
    outfit: [
        {id: 'shirt', label: 'Рубашка', kind: 'top' as const},
        {id: 'pants', label: 'Брюки', kind: 'bottom' as const},
        {id: 'jacket', label: 'Куртка', kind: 'outer' as const},
        {id: 'sneakers', label: 'Кроссовки', kind: 'shoes' as const},
        {id: 'watch', label: 'Часы', kind: 'watch' as const},
    ],
    matchLabel: 'Совпадение с погодой',
    matchValue: '96%',
    explain: 'Почему этот образ?',
} as const;

export const SHOPPING_BLOCK = {
    title: 'Цифровой шопинг',
    text:
        'Подбираем вещи, которые дополнят ваш гардероб. Только то, что сочетается ' +
        'с тем, что у вас уже есть.',
    cta: 'Смотреть идеи',
    items: [
        {id: 's1', kind: 'bottom' as const},
        {id: 's2', kind: 'top' as const},
        {id: 's3', kind: 'watch' as const},
        {id: 's4', kind: 'bag' as const},
    ],
} as const;

export const RATING_BLOCK = {
    titleLead: 'Сохраняйте, ставьте лайки,',
    titleSecond: 'отмечайте любимые.',
    titleAccent: 'Ваш стиль — в ваших руках.',
    growth: '+5,0%',
    growthNote: 'Стиль растёт с каждым образом',
    looks: [
        {id: 'l1', kind: 'outer' as const},
        {id: 'l2', kind: 'bag' as const},
        {id: 'l3', kind: 'top' as const},
        {id: 'l4', kind: 'outer' as const},
    ],
} as const;

export const ECOSYSTEM_BLOCK = {
    titleLead: 'Открытая экосистема —',
    titleAccent: 'образ уже готов.',
    text: 'Подключайте AI, аналитические сервисы и магазины. Настраивайте под себя.',
    tiles: [
        {id: 'ai', title: 'Подключение AI'},
        {id: 'shops', title: 'Магазины и скидки'},
        {id: 'calendar', title: 'Календарь событий'},
        {id: 'integrations', title: 'Интеграции'},
    ],
    note: 'Регулярные обновления',
} as const;

export const STATS_BLOCK = {
    title: 'Статистика гардероба',
    donutValue: '78%',
    donutNote: 'вещей носятся регулярно — остальное видно сразу',
    segments: [
        {id: 'tops', label: 'Топы', percent: 41},
        {id: 'bottoms', label: 'Низ', percent: 25},
        {id: 'outer', label: 'Верхняя одежда', percent: 18},
        {id: 'shoes', label: 'Обувь', percent: 10},
        {id: 'accessories', label: 'Аксессуары', percent: 6},
    ],
    valueLabel: 'Стоимость гардероба',
    valueAmount: '≈420 тыс. ₽',
} as const;

export const INSIGHT_BLOCK = {
    titleLead: 'Наконец видно,',
    titleSecond: 'что происходит',
    titleAccent: 'с вашим гардеробом.',
    tiles: [
        {id: 'total', value: '128', label: 'Всего вещей'},
        {id: 'often', value: '37', label: 'Часто ношу'},
        {id: 'rare', value: '14', label: 'Редко'},
        {id: 'never', value: '14', label: 'Не носил'},
        {id: 'value', value: '≈420', label: 'Стоимость, тыс. ₽'},
        {id: 'seasons', value: '6', label: 'Сезонов'},
        {id: 'categories', value: '8', label: 'Категорий'},
    ],
    radar: [
        {id: 'universal', label: 'Универсальность', value: 0.86},
        {id: 'colors', label: 'Цвета', value: 0.68},
        {id: 'quality', label: 'Качество', value: 0.74},
        {id: 'practical', label: 'Практичность', value: 0.9},
        {id: 'style', label: 'Стиль', value: 0.62},
    ],
} as const;

export const SECURITY_BLOCK = {
    titleLead: 'Контроль',
    titleAccent: 'всегда у вас.',
    text:
        'Кто видит ваши данные — решаете вы. Мы не передаём их третьим лицам, ' +
        'а доступ к гардеробу выдаётся точечно и отзывается в один клик.',
    points: [
        {id: 'encryption', title: 'Шифрование данных', text: 'Чувствительные поля шифруются в базе.'},
        {id: 'ownership', title: 'Данные только у вас', text: 'Экспорт и удаление — по вашему запросу.'},
        {id: 'third-party', title: 'Без передачи третьим лицам', text: 'Никакой продажи данных и скрытых трекеров.'},
        {id: 'policy', title: 'Прозрачная политика', text: 'Понятные правила и журнал доступа.'},
    ],
} as const;

export const DYNAMICS_BLOCK = {
    title: 'Динамика использования',
    value: '+420',
    note: 'носок отмечено за год',
    months: ['Янв', 'Мар', 'Май', 'Июл', 'Сен', 'Ноя'],
    /** Нормированные значения кривой (0..1) для иллюстративного графика. */
    points: [0.12, 0.2, 0.26, 0.38, 0.34, 0.48, 0.56, 0.62, 0.58, 0.72, 0.84, 0.95],
} as const;

export const FINAL_CTA = {
    title: 'Начните со своего шкафа.',
    text: 'Соберите гардероб, сочетайте, экономьте время и выглядите уверенно каждый день.',
    primary: 'Создать мой шкаф',
    secondary: 'Войти',
    notes: ['Бесплатно начать', 'Без карты', 'Можно в любое время'],
} as const;

/** Витринные карточки вещей для мокапа приложения в hero. */
export const MOCK_ITEMS = [
    {id: 'm1', kind: 'top' as const, rating: 4},
    {id: 'm2', kind: 'outer' as const, rating: 3},
    {id: 'm3', kind: 'bag' as const, rating: 5},
    {id: 'm4', kind: 'watch' as const, rating: 4},
    {id: 'm5', kind: 'bottom' as const, rating: 5},
    {id: 'm6', kind: 'shoes' as const, rating: 3},
    {id: 'm7', kind: 'top' as const, rating: 4},
    {id: 'm8', kind: 'outer' as const, rating: 4},
] as const;

export const MOCK_NAV = ['Главная', 'Вещи', 'Образы', 'Сезоны', 'Статистика', 'Календарь'] as const;
export const MOCK_TABS = ['Все', 'Топы', 'Низ', 'Обувь', 'Аксессуары'] as const;

export const MOCK_AI_CARD = {
    title: 'AI-рекомендация',
    subtitle: 'Подойдёт к «Тёплая среда»',
    match: '92% совпадение',
    cta: 'Смотреть больше',
} as const;

export const MOCK_PHONE = {
    title: 'Образ дня',
    subtitle: 'Лёгкий и свежий',
    temp: '+18°',
    save: 'Сохранить образ',
} as const;
